import { SgwtMiniFooterHTMLElement } from '@sgwt/sgwt-widgets-react';
import { useMiniFooterWidget } from 'hooks/use-footer-widget';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { useCallback, useEffect, useState } from 'react';
import { matomoUserConsentChanged, videoUserConsentChanged } from 'state/userConsent/userConsent.actions';

export type UserCookieConsent = {
  hasMatomoConsent: boolean;
  hasVideoConsent: boolean;
};

const getUserConsent = async (footerElement: SgwtMiniFooterHTMLElement) => {
  const [hasMatomoConsent, hasVideoConsent] = await Promise.all([
    footerElement.getUserConsentOnMatomo(),
    footerElement.getUserConsentOnQumu(),
  ]);
  return { hasMatomoConsent, hasVideoConsent } satisfies UserCookieConsent;
};

const canReadUserConsent = (footerElement: SgwtMiniFooterHTMLElement) =>
  typeof footerElement.getUserConsentOnMatomo === 'function' &&
  typeof footerElement.getUserConsentOnQumu === 'function';

export function useUserCookieConsent() {
  const miniFooterElement = useMiniFooterWidget();
  const [consent, setConsent] = useState<UserCookieConsent>();

  useEffect(() => {
    if (!miniFooterElement || !canReadUserConsent(miniFooterElement)) {
      return;
    }
    let isUnmounted = false;

    const emitConsent = () => {
      getUserConsent(miniFooterElement).then(consent => {
        if (!isUnmounted) {
          setConsent(consent);
        }
      });
    };

    emitConsent();

    miniFooterElement.addEventListener('sgwt-mini-footer--user-consent-changed', emitConsent);

    return () => {
      isUnmounted = true;
      miniFooterElement.removeEventListener('sgwt-mini-footer--user-consent-changed', emitConsent);
    };
  }, [miniFooterElement]);

  const openCookiesConsent = useCallback(() => {
    miniFooterElement?.displayUserConsentPopup();
  }, [miniFooterElement]);

  return { openCookiesConsent, ...consent };
}

export function useSyncUserCookieConsentWithStore() {
  const dispatch = useThunkDispatch();
  const { hasMatomoConsent, hasVideoConsent } = useUserCookieConsent();
  const { hasGivenConsentToVideo, hasGivenConsentToMatomoUserTracking } = useAppSelector(state => state.userConsent);

  useEffect(() => {
    if (hasMatomoConsent === undefined) {
      return;
    }
    if (hasMatomoConsent !== hasGivenConsentToMatomoUserTracking) {
      dispatch(matomoUserConsentChanged(hasMatomoConsent));
    }
  }, [dispatch, hasMatomoConsent, hasGivenConsentToMatomoUserTracking]);

  useEffect(() => {
    if (hasVideoConsent === undefined) {
      return;
    }
    if (hasVideoConsent !== hasGivenConsentToVideo) {
      dispatch(videoUserConsentChanged(hasVideoConsent));
    }
  }, [dispatch, hasVideoConsent, hasGivenConsentToVideo]);
}
